import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useRegisterRefresh } from '../context/PullToRefreshContext';

function StatusBadge({ status }) {
  if (status === 'resolved') {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 shrink-0">Çözüldü</span>;
  }
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 shrink-0">Açık</span>;
}

function FileRow({ f, canDelete, onDelete }) {
  return (
    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
      <svg className="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <a
        href={f.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 min-w-0 text-xs text-brand-600 font-medium truncate hover:underline"
      >
        {f.original_name}
      </a>
      {canDelete && (
        <button type="button" onClick={onDelete} className="shrink-0 text-gray-400 hover:text-red-500 transition-colors">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

function SelectedFileRow({ name, onRemove }) {
  return (
    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
      <svg className="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <span className="flex-1 min-w-0 text-xs text-gray-700 truncate">{name}</span>
      <button type="button" onClick={onRemove} className="shrink-0 text-gray-400 hover:text-red-500 transition-colors">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default function FeedbackPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_superuser;

  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);

  // Yeni geri bildirim form
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [newFiles, setNewFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Yanıt
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const fetchFeedbacks = useCallback(async () => {
    try {
      const { data } = await api.getFeedbacks();
      setFeedbacks(Array.isArray(data) ? data : data.results || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFeedbacks(); }, [fetchFeedbacks]);

  const openDetail = async (fb) => {
    setDetail({ ...fb, replies: fb.replies || [] });
    setDetailLoading(true);
    setReplyText('');
    try {
      const { data } = await api.getFeedback(fb.id);
      setDetail(data);
      // Refresh unread count after marking replies as read
      api.getFeedbackUnreadCount()
        .then(({ data: d }) => {
          window.dispatchEvent(new CustomEvent('feedback-unread-changed', { detail: { count: d.unread_count || 0 } }));
        })
        .catch(() => {});
    } finally {
      setDetailLoading(false);
    }
  };

  const createFeedback = async () => {
    if (!title.trim() || !body.trim()) {
      setFormError('Başlık ve metin zorunludur.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const { data } = await api.createFeedback({ title: title.trim(), body: body.trim(), files: newFiles });
      setFeedbacks(prev => [data, ...prev]);
      setTitle('');
      setBody('');
      setNewFiles([]);
      setShowNewModal(false);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Gönderim başarısız.');
    } finally {
      setSubmitting(false);
    }
  };

  const addReply = async () => {
    if (!replyText.trim() || !detail) return;
    setReplying(true);
    try {
      const { data } = await api.replyFeedback(detail.id, { body: replyText.trim() });
      setDetail(data);
      setFeedbacks(prev => prev.map(f =>
        f.id === data.id ? { ...f, reply_count: data.replies?.length || 0 } : f
      ));
      setReplyText('');
    } finally {
      setReplying(false);
    }
  };

  const toggleStatus = async () => {
    if (!detail || !isAdmin) return;
    const newStatus = detail.status === 'resolved' ? 'open' : 'resolved';
    try {
      const { data } = await api.updateFeedbackStatus(detail.id, newStatus);
      setDetail(data);
      setFeedbacks(prev => prev.map(f => f.id === data.id ? { ...f, status: data.status } : f));
    } catch {}
  };

  const deleteFile = async (fileId) => {
    try {
      await api.deleteFeedbackFile(fileId);
      setDetail(d => d ? { ...d, files: d.files.filter(f => f.id !== fileId) } : d);
    } catch {}
  };

  return (
    <div className="px-4 py-4 pb-24">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-500">Sorun bildirin veya öneri gönderin</p>
        <button
          type="button"
          className="btn-primary !py-1.5 !px-4 !text-xs"
          onClick={() => { setTitle(''); setBody(''); setNewFiles([]); setFormError(''); setShowNewModal(true); }}
        >
          + Yeni
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-7 h-7 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && feedbacks.length === 0 && (
        <div className="text-center py-16">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <p className="text-gray-400 text-sm">Henüz geri bildirim yok.</p>
          <p className="text-gray-300 text-xs mt-1">Bir sorun veya öneri iletmek için "+ Yeni" butonunu kullanın.</p>
        </div>
      )}

      <div className="space-y-2">
        {feedbacks.map(fb => (
          <button
            key={fb.id}
            type="button"
            onClick={() => openDetail(fb)}
            className="card w-full text-left active:scale-[0.98] transition-transform"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm font-semibold text-gray-900 flex-1 min-w-0">{fb.title}</p>
              <StatusBadge status={fb.status} />
            </div>
            <p className="text-xs text-gray-500 line-clamp-2 mb-2">{fb.body}</p>
            <div className="flex items-center gap-3 text-[10px] text-gray-400">
              <span>{fb.created_by_name}</span>
              <span>·</span>
              <span>{format(new Date(fb.created_at), 'd MMM yyyy', { locale: tr })}</span>
              {fb.reply_count > 0 && (
                <>
                  <span>·</span>
                  <span>{fb.reply_count} yanıt</span>
                </>
              )}
              {fb.files?.length > 0 && (
                <>
                  <span>·</span>
                  <span>{fb.files.length} dosya</span>
                </>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Detay Alt Sayfası */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-end z-[80]" onClick={() => setDetail(null)}>
          <div
            className="bg-white w-full max-w-md mx-auto rounded-t-2xl p-4 max-h-[88vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Başlık + Durum */}
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 flex-1">{detail.title}</h3>
              <StatusBadge status={detail.status} />
            </div>

            {/* Admin: durum değiştir */}
            {isAdmin && (
              <button
                type="button"
                onClick={toggleStatus}
                className={`text-xs font-medium mb-2 ${detail.status === 'resolved' ? 'text-amber-600' : 'text-green-600'}`}
              >
                {detail.status === 'resolved' ? '↩ Yeniden Aç' : '✓ Çözüldü Olarak İşaretle'}
              </button>
            )}

            {/* Gövde */}
            <p className="text-sm text-gray-700 mb-1 whitespace-pre-wrap">{detail.body}</p>
            <p className="text-xs text-gray-400 mb-3">
              {detail.created_by_name} · {format(new Date(detail.created_at), 'd MMMM yyyy HH:mm', { locale: tr })}
            </p>

            {/* Dosyalar */}
            {detail.files?.length > 0 && (
              <div className="flex flex-col gap-1.5 mb-4">
                {detail.files.map(f => (
                  <FileRow
                    key={f.id}
                    f={f}
                    canDelete={isAdmin || detail.created_by === user?.id}
                    onDelete={() => deleteFile(f.id)}
                  />
                ))}
              </div>
            )}

            {/* Yükleniyor */}
            {detailLoading && (
              <div className="flex justify-center py-3">
                <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* Yanıtlar */}
            {!detailLoading && detail.replies?.length > 0 && (
              <div className="border-t border-gray-100 pt-3 mb-3 space-y-2">
                <p className="text-xs font-semibold text-gray-500 mb-2">Yanıtlar</p>
                {detail.replies.map((r, i) => (
                  <div
                    key={i}
                    className={`rounded-xl px-3 py-2.5 ${
                      r.is_admin_reply
                        ? 'bg-brand-50 border border-brand-100'
                        : 'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <p className="text-[10px] font-semibold text-gray-500 mb-0.5">
                      {r.is_admin_reply ? 'Admin' : r.created_by_name}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{r.body}</p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      {format(new Date(r.created_at), 'd MMM yyyy HH:mm', { locale: tr })}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Yanıt formu — açık ticketlar için */}
            {!detailLoading && detail.status !== 'resolved' && (
              <div className="border-t border-gray-100 pt-3">
                <textarea
                  className="input min-h-[80px] resize-none mb-2 text-sm"
                  placeholder="Yanıt yaz..."
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                />
                <button
                  className="btn-primary"
                  onClick={addReply}
                  disabled={replying || !replyText.trim()}
                >
                  {replying ? 'Gönderiliyor...' : 'Yanıt Gönder'}
                </button>
              </div>
            )}

            <button type="button" className="btn-secondary mt-2" onClick={() => setDetail(null)}>
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* Yeni Geri Bildirim Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end z-[80]">
          <div className="bg-white w-full max-w-md mx-auto rounded-t-2xl p-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-3">Yeni Geri Bildirim</h3>

            <input
              className="input mb-2"
              placeholder="Başlık *"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
            />
            <textarea
              className="input min-h-[120px] resize-none mb-3"
              placeholder="Açıklama — ne gördünüz, ne bekliyordunuz?"
              value={body}
              onChange={e => setBody(e.target.value)}
            />

            <div className="mb-2 flex items-center gap-3">
              <input
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                multiple
                id="fb-file-input"
                className="hidden"
                onChange={e => {
                  const selected = Array.from(e.target.files || []);
                  e.target.value = '';
                  setNewFiles(prev => [...prev, ...selected].slice(0, 5));
                }}
              />
              <label
                htmlFor="fb-file-input"
                className="inline-flex items-center justify-center px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 bg-gray-50 hover:border-brand-400 hover:text-brand-600 cursor-pointer"
              >
                Dosya / Görsel Ekle
              </label>
              {newFiles.length > 0 && (
                <span className="text-xs text-gray-500">{newFiles.length}/5</span>
              )}
            </div>

            {newFiles.length > 0 && (
              <div className="flex flex-col gap-1 mb-3">
                {newFiles.map((f, i) => (
                  <SelectedFileRow
                    key={i}
                    name={f.name}
                    onRemove={() => setNewFiles(prev => prev.filter((_, j) => j !== i))}
                  />
                ))}
              </div>
            )}

            {formError && <p className="text-xs text-red-600 mb-2">{formError}</p>}

            <button
              className="btn-primary"
              onClick={createFeedback}
              disabled={submitting || !title.trim() || !body.trim()}
            >
              {submitting ? 'Gönderiliyor...' : 'Gönder'}
            </button>
            <button
              type="button"
              className="btn-secondary mt-2"
              onClick={() => setShowNewModal(false)}
            >
              İptal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
